/* John Boyle 2024 */
import './style.css';
import Map from 'ol/Map';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import ImageWMS from 'ol/source/ImageWMS.js';
import TileWMS from 'ol/source/TileWMS.js';
import View from 'ol/View';
import XYZ from 'ol/source/XYZ';
import {fromLonLat} from 'ol/proj';
import ImageLayer from 'ol/layer/Image';
import Static from 'ol/source/ImageStatic';
import {getCenter} from 'ol/extent';
import {Control, Attribution, defaults as defaultControls} from 'ol/control';
import {createStringXY} from 'ol/coordinate.js';
import MousePosition from 'ol/control/MousePosition.js';

import proj4 from 'proj4';
import {register} from 'ol/proj/proj4';
import {get as getProjection} from 'ol/proj';

function leftFillNum(num, targetLength) {
    return num.toString().padStart(targetLength, 0);
}

function jetOnWhiteInterpolator(x) {
   let r, g, b;
    if (x >= 0.375 && x < 0.625) {
       r = 4.0 * x - 1.5;
    } else if (x >= 0.625 && x < 0.875) {
       r = 1.0;
    } else if (x >= 0.875) {
       r = -4.0 * x + 4.5;
    } else {
       r = 0.0;
    }

    // green channel
    if (x >= 0.125 && x < 0.375) {
       g = 4.0 * x - 0.5;
    } else if (x >= 0.375 && x < 0.625) {
       g = 1.0;
    } else if (x >= 0.625 && x < 0.875) {
       g = -4.0 * x + 3.5;
    } else {
       g = 0.0;
    }

    // blue channel
    if (x < 0.125) {
       b = 4.0 * x + 0.5;
    } else if (x >= 0.125 && x < 0.375) {
       b = 1.0;
    } else if (x >= 0.375 && x < 0.625) {
       b = -4.0 * x + 2.5;
    } else {
       b = 0.0;
    }

    if (x < 0.25) {
       r = 1.0 - 4.0 * x + 4.0 * x * r;
       g = 1.0 - 4.0 * x + 4.0 * x * g;
       b = 1.0 - 4.0 * x + 4.0 * x * b;
    }

    return [255 * r, 255 * g, 255 * b];
};

let jet_colors = [];
for (let i = 0; i <= 1.0; i += 0.1) {
    jet_colors.push(jetOnWhiteInterpolator(i));
}

let jet_scale = chroma.scale(jet_colors);

/* Animation */
let leftBottom = [-125.55, 48.1];
let rightTop = [-122.43, 49.5];
let extent = fromLonLat(leftBottom).concat(fromLonLat(rightTop))

let img = [new Static({
    opacity: 1,
    url: 'out0000.png',
    imageExtent: extent,
})];

let N_img = 106;
let load_interval = 20;

function load_more_images(count) {
    let len = img.length;
    for (let i = len; i < (len + count); i++) {
        if (i >= N_img) return;
        let imgPath = 'out' + leftFillNum(i, 4) + '.png';
        img.push(new Static({
            url: imgPath,
            imageExtent: extent,
        }));
    }
 };

/* Begin loading images */
load_more_images(load_interval);
         
/* layer for the images */
const staticImageLayer = new ImageLayer({
    source: img[0],
    projection: 'EPSG:3857'
});

/* Animation Setup */
let img_count = 0;
let delay = 250;
let paused = 0;
let increment = 1;
function animate() {
    if (paused) return;
    staticImageLayer.setSource(img[img_count]);
//    document.getElementById('count').innerHTML = 'Applied Ocean Sciences - Acoustic Simulations - ' + leftFillNum(img_count, 4);
    
    img_count += increment;
    constrain_img_count();
    
    setTimeout(animate, delay);
    
    if (img_count == img.length) {
        load_more_images(load_interval);
    }
}

/* "forward" and "reverse" assume the initial animation direction is in the
 * positive direction from frame index 0. */
function constrain_img_count() {
    let hit_end = false;
    if (img_count < 0) {
        img_count = 0;
        hit_end = true;
    }
    
    if (img_count > (N_img - 1)) {
        img_count = N_img - 1;
        hit_end = true;
    }
    
    if (hit_end) {
        if (-1 == increment) {
            increment = 1;
        }
        else if (1 == increment) {
            increment = -1;
        }
    }
}

function img_count_FF() {
    img_count += increment;
    constrain_img_count();
    staticImageLayer.setSource(img[img_count]);
}

function img_count_RW() {
    img_count -= increment;
    constrain_img_count();
    staticImageLayer.setSource(img[img_count]);
}

/* Animation Control Buttons */
class PauseAnimationControl extends Control {
    constructor(opt_options) {
        const options = opt_options || {};
        const button_pause = document.createElement('button');
        button_pause.className = 'transport-controls-pause';
        button_pause.innerHTML = '⏯';
        
        const button_backward = document.createElement('button');
        button_backward.className = 'transport-controls-back';
        button_backward.innerHTML = '⏮';
        button_backward.dataset['action'] = "RW";
        
        const button_forward = document.createElement('button');
        button_forward.className = 'transport-controls-forward';
        button_forward.innerHTML = '⏭';
        button_forward.dataset['action'] = "FF";
        
        const element = document.createElement('div');
        element.className = 'transport-controls ol-selectable ol-control';
        element.appendChild(button_backward);
        element.appendChild(button_pause);
        element.appendChild(button_forward);

        super({
            element: element,
            target: options.target,
        });
        
        button_pause.addEventListener(   'click', this.handlePauseAnimation.bind(this), false);
        button_forward.addEventListener( 'click', this.handleForwardBack.bind(this), false);
        button_backward.addEventListener('click', this.handleForwardBack.bind(this), false);
    }
    
    handlePauseAnimation() {
        paused = paused ? 0 : 1; // toggle
        if (paused) {
            this.element.children[1].innerHTML = '⏵';
        } else {
            this.element.children[1].innerHTML = '⏸';
            animate();
        }
    };
    
    handleForwardBack(event) {
        let action = event.target.dataset.action;
        if (action) {
            /* pause first */
            if (!paused) paused = 1;
            load_more_images(N_img);
            if ("FF" === action) { img_count_FF(); }
            if ("RW" === action) { img_count_RW(); }
        }
    };
}

/* Keyboard controls for play/pause/FF/RW */
function FF_RW(e) {
    if (!paused && 'Space' == e.code) {
        paused = 1;
        load_more_images(N_img); /* load them all - assume someone will want to see them all */
    }
    
    if (paused && ('ArrowRight' == e.code)) img_count_FF();
    if (paused && ('ArrowLeft' == e.code))  img_count_RW();
}

document.body.addEventListener('keydown', FF_RW);

/* display coordinates under mouse pointer */
const mousePositionControl = new MousePosition({
  coordinateFormat: createStringXY(4),
  projection: 'EPSG:4326',
});

/* The Map */
let map = new Map({
controls: defaultControls().extend([new PauseAnimationControl(), mousePositionControl]),
  target: 'map',
  layers: [
    new TileLayer({
//        preload: 4,
    source: new OSM(),
    }),
  ],
  view: new View({
    center: getCenter(extent),
    zoom: 8,
  }),
});

/* make sure Attributions is not collapsed - per esri map rules if using esri tiles */
map.controls.getArray()[2].setCollapsed(false);

map.addLayer(staticImageLayer);

/* Start animation */
animate();


/* Add a colorbar to the map div */
let colorbar = document.createElement('div');
colorbar.className = 'gradient';

for (let i = 0.0; i < 200; i++) {
    let s = document.createElement('span');
    s.className = 'grad-step';
    s.style.backgroundColor = jet_scale(i/200);
    colorbar.append(s);
}

let cmin = 110, cmax = 50, ctick = -10;
for (let i = 0; i < 7; i++) {
    let cspan = cmax - cmin;
    let tick = document.createElement('span');
    tick.className = 'tick';
    tick.style.right = String(99 * (1 - i * 0.1666)) + '%';
    tick.innerHTML = String(cmin + i * ctick);
    if (0 == i) {
        tick.style.right = '0%';
        tick.style.left = '0%';
    }
    colorbar.append(tick);
}

document.getElementById('scenario_colorbar').append(colorbar);
let colorbarTitle = document.createElement('div');
colorbarTitle.id = 'colorbar-title';
colorbarTitle.innerHTML = "Transmission Loss (dB re m²)";

document.getElementById('scenario_colorbar').append(colorbarTitle);
                                        
